import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { AuthUser, LoginResponse } from "../types/auth";
import { useFormDraftStore } from "./formDraftStore";

interface AuthStoreState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  setSession: (session: LoginResponse) => void;
  setUser: (user: AuthUser) => void;
  updateTokens: (tokens: { access: string; refresh?: string }) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
  clearSession: () => void;
}

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      setSession: (session) =>
        set({
          accessToken: session.access,
          refreshToken: session.refresh,
          user: session.user,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
        }),
      setUser: (user) =>
        set((state) => ({
          user,
          isAuthenticated: Boolean(state.accessToken && state.refreshToken),
        })),
      updateTokens: (tokens) =>
        set((state) => {
          const refreshToken = tokens.refresh ?? state.refreshToken;
          return {
            accessToken: tokens.access,
            refreshToken,
            isAuthenticated: Boolean(tokens.access && refreshToken),
          };
        }),
      setLoading: (isLoading) => set({ isLoading }),
      setInitialized: (isInitialized) => set({ isInitialized }),
      clearSession: () =>
        {
          useFormDraftStore.getState().clearAllDrafts();
          set({
            accessToken: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }
        state.isLoading = false;
        state.isInitialized = false;
        state.isAuthenticated = Boolean(state.accessToken && state.refreshToken);
      },
    },
  ),
);
